import httpClient from './httpClient';

const API_BASE_URL = '/configuracion-inicial-vacaciones';

export const configuracionInicialVacacionesService = {
  /**
   * Obtener todas las configuraciones de saldo inicial para una empresa
   * @param {number} empresaId - ID de la empresa
   * @returns {Promise} Respuesta con las configuraciones
   */
  async obtenerConfiguraciones(empresaId) {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/${empresaId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener configuraciones de saldo inicial:', error);
      throw error;
    }
  },

  /**
   * Obtener configuración específica para un empleado
   * @param {number} empresaId - ID de la empresa
   * @param {number} empleadoId - ID del empleado
   * @returns {Promise} Respuesta con la configuración del empleado
   */
  async obtenerConfiguracionEmpleado(empresaId, empleadoId) {
    try {
      const response = await httpClient.get(`${API_BASE_URL}/${empresaId}/${empleadoId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener configuración del empleado:', error);
      throw error;
    }
  },

  /**
   * Obtener configuraciones múltiples para varios empleados
   * @param {number} empresaId - ID de la empresa
   * @param {Array<number>} empleadosIds - Array de IDs de empleados
   * @returns {Promise} Respuesta con las configuraciones múltiples
   */
  async obtenerConfiguracionesMultiples(empresaId, empleadosIds) {
    try {
      const response = await httpClient.post(`${API_BASE_URL}/${empresaId}/multiple`, {
        empleados: empleadosIds
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener configuraciones múltiples:', error);
      throw error;
    }
  },

  /**
   * Crear o actualizar configuración de saldo inicial
   * @param {number} empresaId - ID de la empresa
   * @param {number} empleadoId - ID del empleado
   * @param {Object} data - Datos de la configuración
   * @param {number} data.dias - Días de saldo inicial
   * @param {string} data.observaciones - Observaciones sobre el ajuste
   * @returns {Promise} Respuesta con la configuración guardada
   */
  async guardarConfiguracion(empresaId, empleadoId, data) {
    try {
      const response = await httpClient.post(`${API_BASE_URL}/${empresaId}/${empleadoId}`, {
        dias: data.dias,
        observaciones: data.observaciones || null
      });
      return response.data;
    } catch (error) {
      console.error('Error al guardar configuración de saldo inicial:', error);
      throw error;
    }
  },

  /**
   * Eliminar configuración de saldo inicial
   * @param {number} empresaId - ID de la empresa
   * @param {number} empleadoId - ID del empleado
   * @returns {Promise} Respuesta de la eliminación
   */
  async eliminarConfiguracion(empresaId, empleadoId) {
    try {
      const response = await httpClient.delete(`${API_BASE_URL}/${empresaId}/${empleadoId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar configuración de saldo inicial:', error);
      throw error;
    }
  },

  /**
   * Guardar múltiples configuraciones de saldo inicial
   * @param {number} empresaId - ID de la empresa
   * @param {Array<Object>} configuraciones - Array de configuraciones
   * @param {number} configuraciones[].empleadoId - ID del empleado
   * @param {number} configuraciones[].dias - Días de saldo inicial
   * @param {string} configuraciones[].observaciones - Observaciones
   * @returns {Promise} Resultados de las operaciones
   */
  async guardarConfiguracionesMultiples(empresaId, configuraciones) {
    try {
      const promesas = configuraciones.map(config => 
        this.guardarConfiguracion(empresaId, config.empleadoId, {
          dias: config.dias,
          observaciones: config.observaciones
        })
      );

      const resultados = await Promise.allSettled(promesas);
      
      const exitosos = resultados.filter(r => r.status === 'fulfilled').length;
      const fallidos = resultados.filter(r => r.status === 'rejected').length;

      return {
        success: true,
        message: `${exitosos} configuraciones guardadas exitosamente${fallidos > 0 ? `, ${fallidos} fallaron` : ''}`,
        exitosos,
        fallidos,
        resultados
      };
    } catch (error) {
      console.error('Error al guardar configuraciones múltiples:', error);
      throw error;
    }
  }
};

export default configuracionInicialVacacionesService;

